import { useState, useEffect, useMemo } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { supabase } from "@/integrations/supabase/client";
import {
  Activity,
  Search,
  RefreshCw,
  LogIn,
  LogOut,
  Wrench,
  Download,
} from "lucide-react";

interface ActivityLog {
  id: string;
  user_id: string;
  activity_type: string;
  tool_name: string | null;
  created_at: string;
  user_email: string;
  user_name: string;
  action_details: Record<string, any>;
}

type FilterType = "all" | "login" | "logout" | "tool_usage" | "api_call";

export default function AdminActivity() {
  const [logs, setLogs] = useState<ActivityLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState<FilterType>("all");

  const fetchActivityLogs = async () => {
    setLoading(true);
    try {
      // Fetch admin user IDs to exclude them
      const { data: adminRoles } = await supabase
        .from("user_roles")
        .select("user_id")
        .eq("role", "admin");

      const adminUserIds = new Set((adminRoles || []).map((r) => r.user_id));

      // Fetch activity logs
      const { data: activityLogs } = await supabase
        .from("user_activity_logs")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(500);

      // Fetch profiles for user info
      const { data: profiles } = await supabase
        .from("profiles")
        .select("id, email, first_name, last_name, display_name");

      // Filter out admin users from activity logs
      const filteredLogs = (activityLogs || []).filter(
        (log) => log.user_id && !adminUserIds.has(log.user_id)
      );

      const logsWithUserInfo: ActivityLog[] = filteredLogs
        .map((log) => {
          const profile = profiles?.find((p) => p.id === log.user_id);
          // Skip orphaned logs (no matching profile)
          if (!profile) return null;
          
          const actionDetails = (log.action_details as Record<string, any>) || {};

          return {
            id: log.id,
            user_id: log.user_id,
            activity_type: log.activity_type,
            tool_name: log.tool_name,
            created_at: log.created_at,
            user_email: profile.email || actionDetails?.email || "Unknown",
            user_name:
              profile.display_name ||
              `${profile.first_name || ""} ${profile.last_name || ""}`.trim() ||
              "Unknown",
            action_details: actionDetails,
          };
        })
        .filter((log): log is ActivityLog => log !== null);

      setLogs(logsWithUserInfo);
    } catch (error) {
      console.error("Error fetching activity logs:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchActivityLogs();
  }, []);

  const filteredLogs = useMemo(() => {
    return logs.filter((log) => {
      const matchesSearch =
        !searchTerm ||
        log.user_email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        log.user_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        log.tool_name?.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesFilter = filterType === "all" || log.activity_type === filterType;

      return matchesSearch && matchesFilter;
    });
  }, [logs, searchTerm, filterType]);

  const getActivityIcon = (type: string) => {
    switch (type) {
      case "login":
        return <LogIn className="h-4 w-4 text-success" />;
      case "logout":
        return <LogOut className="h-4 w-4 text-warning" />;
      case "tool_usage":
        return <Wrench className="h-4 w-4 text-info" />;
      default:
        return <Activity className="h-4 w-4 text-muted-foreground" />;
    }
  };

  const getActivityBadge = (type: string) => {
    switch (type) {
      case "login":
        return <Badge className="bg-success text-success-foreground">Login</Badge>;
      case "logout":
        return <Badge variant="secondary">Logout</Badge>;
      case "tool_usage":
        return <Badge className="bg-info text-info-foreground">Tool</Badge>;
      case "api_call":
        return <Badge variant="outline">API</Badge>;
      default:
        return <Badge variant="outline">{type}</Badge>;
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;

    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const exportLogsCSV = () => {
    const headers = ["Date", "User", "Email", "Activity", "Tool", "Details"];
    const rows = filteredLogs.map((log) => [
      new Date(log.created_at).toISOString(),
      log.user_name,
      log.user_email,
      log.activity_type,
      log.tool_name || "",
      JSON.stringify(log.action_details),
    ]);

    const csv = [headers, ...rows].map((row) => row.map((c) => `"${c}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `activity-logs-${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
  };

  // Activity stats
  const stats = useMemo(() => {
    const today = new Date().toDateString();
    const todayLogs = logs.filter(
      (l) => new Date(l.created_at).toDateString() === today
    );

    return {
      totalToday: todayLogs.length,
      loginsToday: todayLogs.filter((l) => l.activity_type === "login").length,
      toolUsageToday: todayLogs.filter((l) => l.activity_type === "tool_usage").length,
      uniqueUsersToday: new Set(todayLogs.map((l) => l.user_id)).size,
    };
  }, [logs]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between bg-gradient-to-r from-accent/5 via-card to-info/5 rounded-xl p-6 shadow-sm border border-accent/10">
        <div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-accent to-info bg-clip-text text-transparent">Activity Logs</h1>
          <p className="text-muted-foreground">Monitor user activity and usage</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={exportLogsCSV} className="bg-background hover:bg-success/10 hover:text-success hover:border-success/30">
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
          <Button variant="outline" onClick={fetchActivityLogs} disabled={loading} className="bg-background hover:bg-primary/10 hover:text-primary hover:border-primary/30">
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card className="bg-gradient-to-br from-primary/5 to-card shadow-sm border-l-4 border-l-primary border hover:shadow-lg transition-all duration-300 hover:-translate-y-0.5">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-primary/10 ring-2 ring-primary/20">
              <Activity className="h-5 w-5 text-primary" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Events Today</p>
              <p className="text-lg font-semibold text-foreground">{stats.totalToday}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-success/5 to-card shadow-sm border-l-4 border-l-success border hover:shadow-lg transition-all duration-300 hover:-translate-y-0.5">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-success/10 ring-2 ring-success/20">
              <LogIn className="h-5 w-5 text-success" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Logins Today</p>
              <p className="text-lg font-semibold text-foreground">{stats.loginsToday}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-info/5 to-card shadow-sm border-l-4 border-l-info border hover:shadow-lg transition-all duration-300 hover:-translate-y-0.5">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-info/10 ring-2 ring-info/20">
              <Wrench className="h-5 w-5 text-info" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Tool Usage</p>
              <p className="text-lg font-semibold text-foreground">{stats.toolUsageToday}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-accent/5 to-card shadow-sm border-l-4 border-l-accent border hover:shadow-lg transition-all duration-300 hover:-translate-y-0.5">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-accent/10 ring-2 ring-accent/20">
              <Activity className="h-5 w-5 text-accent" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Active Users</p>
              <p className="text-lg font-semibold text-foreground">{stats.uniqueUsersToday}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="bg-gradient-to-r from-muted/30 to-card shadow-sm border border-muted">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by user, email, or tool..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 bg-background border-muted focus:border-primary"
              />
            </div>
            <Select value={filterType} onValueChange={(v) => setFilterType(v as FilterType)}>
              <SelectTrigger className="w-[180px] bg-background border-muted">
                <SelectValue placeholder="Filter by type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Activities</SelectItem>
                <SelectItem value="login">Logins</SelectItem>
                <SelectItem value="logout">Logouts</SelectItem>
                <SelectItem value="tool_usage">Tool Usage</SelectItem>
                <SelectItem value="api_call">API Calls</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Activity Table */}
      <Card className="bg-card shadow-sm border overflow-hidden">
        <CardHeader className="bg-gradient-to-r from-info/10 via-info/5 to-transparent border-b border-info/10">
          <CardTitle className="flex items-center gap-2 text-foreground">
            <div className="p-1.5 rounded-lg bg-info/10">
              <Activity className="h-4 w-4 text-info" />
            </div>
            Recent Activity
          </CardTitle>
          <CardDescription>
            Showing {filteredLogs.length} activities
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12"></TableHead>
                <TableHead>Time</TableHead>
                <TableHead>User</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Activity</TableHead>
                <TableHead>Details</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8">
                    <div className="flex items-center justify-center gap-2">
                      <RefreshCw className="h-4 w-4 animate-spin" />
                      Loading activity logs...
                    </div>
                  </TableCell>
                </TableRow>
              ) : filteredLogs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                    No activity logs found
                  </TableCell>
                </TableRow>
              ) : (
                filteredLogs.slice(0, 100).map((log) => (
                  <TableRow key={log.id}>
                    <TableCell>{getActivityIcon(log.activity_type)}</TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {formatDate(log.created_at)}
                    </TableCell>
                    <TableCell className="font-medium">{log.user_name}</TableCell>
                    <TableCell className="text-muted-foreground">{log.user_email}</TableCell>
                    <TableCell>{getActivityBadge(log.activity_type)}</TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {log.tool_name || "-"}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
